import { useQuery } from "@tanstack/react-query";
import customerServiceService from "../services/customerServiceService";

/**
 * Custom hook for fetching and caching customer service assignments
 *
 * Features:
 * - Automatically caches data for 5 minutes per customer
 * - Shows cached data immediately on mount
 * - Refetches in background to check for updates
 * - Only re-renders if data actually changed
 * - Separate cache for each customer ID
 *
 * @param {string|number} customerId - The customer ID
 * @returns {Object} Query result with data, loading states, and refetch function
 */
export const useCustomerServices = (customerId) => {
  return useQuery({
    queryKey: ["customerServices", customerId],
    queryFn: async () => {
      if (!customerId) {
        throw new Error("Customer ID is required");
      }
      const services = await customerServiceService.getCustomerServices(
        customerId
      );
      return services;
    },
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Refetch on component mount
    enabled: !!customerId, // Only run query if customerId exists
  });
};
